import { getSessionToken } from '@descope/react-sdk';
import axios from 'axios';

const nonDefault = (value, def) => (value != null && value !== def ? value : undefined);

export const aiPromptStartApi = async ({ prompt, model, webSearch, systemPrompt, temperature, topP, maxOutputTokens, effort }) => {
  const response = await axios.post('admin/ai/prompt', {
    prompt,
    model: model || undefined,
    webSearch: webSearch || undefined,
    systemPrompt: systemPrompt || undefined,
    temperature: nonDefault(temperature, 1.0),
    topP: nonDefault(topP, 1.0),
    maxOutputTokens: maxOutputTokens || undefined,
    effort: nonDefault(effort, 'high'),
  });
  return response.data;
};

export const aiPromptJobApi = async (jobId) => {
  const response = await axios.get(`admin/ai/prompt/job/${jobId}`);
  return response.data; // { status, result?, error? }
};

// Streams the response token-by-token via SSE.
// onDelta(text)  — called for each token chunk
// onDone(result) — called with final { text, citations, ... } on completion
// onError(msg)   — called with error string on failure
export const aiPromptStreamApi = ({ prompt, model, webSearch, systemPrompt, temperature, topP, maxOutputTokens, effort, onDelta, onDone, onError, signal }) => {
  const token = getSessionToken();
  const baseURL = process.env.REACT_APP_API_URL + '/api';

  fetch(`${baseURL}/admin/ai/prompt/stream`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      'x-app-type': process.env.REACT_APP_APP_TYPE || '',
    },
    body: JSON.stringify({
      prompt,
      model: model || undefined,
      webSearch: webSearch || undefined,
      systemPrompt: systemPrompt || undefined,
      temperature: nonDefault(temperature, 1.0),
      topP: nonDefault(topP, 1.0),
      maxOutputTokens: maxOutputTokens || undefined,
      effort: nonDefault(effort, 'high'),
    }),
    signal,
  })
    .then(async (resp) => {
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        onError?.(err.error || `HTTP ${resp.status}`);
        return;
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let currentEvent = '';

      const processBlock = (block) => {
        let eventName = 'message';
        let dataStr = '';
        for (const line of block.split('\n')) {
          if (line.startsWith('event:')) {
            eventName = line.slice(6).trim();
          } else if (line.startsWith('data:')) {
            dataStr = line.slice(5).trim();
          }
        }
        if (!dataStr) return;
        let data;
        try { data = JSON.parse(dataStr); } catch { return; }
        if (eventName === 'delta') onDelta?.(data.text);
        else if (eventName === 'done') onDone?.(data);
        else if (eventName === 'error') onError?.(data.error);
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const blocks = buffer.split('\n\n');
        buffer = blocks.pop(); // last (possibly incomplete) block
        for (const block of blocks) {
          if (block.trim()) processBlock(block);
        }
      }
      // flush remainder
      if (buffer.trim()) processBlock(buffer);
    })
    .catch((err) => {
      if (err?.name === 'AbortError') return;
      onError?.(err?.message || 'Stream failed');
    });
};
